'use client';

import { useState, useCallback } from 'react';
import { BrainDump } from './BrainDump';
import { IdeaOrganizer } from './IdeaOrganizer';
import { GuidedQuestions } from './GuidedQuestions';
import * as api from '@/lib/foundry/api';
import type {
  RawIdea,
  OrganizedIdeas,
  GuidedQuestion,
} from '@/lib/foundry/types';

type WizardStage = 'braindump' | 'organizing' | 'organized' | 'questions' | 'generating';

interface IdeaCaptureWizardProps {
  planId: string;
  onCanvasGenerated: () => void;
  onSkip?: () => void;
}

export function IdeaCaptureWizard({ planId, onCanvasGenerated, onSkip }: IdeaCaptureWizardProps) {
  const [stage, setStage] = useState<WizardStage>('braindump');
  const [rawIdeas, setRawIdeas] = useState<RawIdea[]>([]);
  const [organized, setOrganized] = useState<OrganizedIdeas | null>(null);
  const [questions, setQuestions] = useState<GuidedQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [coverageScore, setCoverageScore] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Handle organizing ideas
  const handleOrganize = useCallback(async () => {
    setStage('organizing');
    setError(null);

    try {
      const result = await api.organizeIdeas(planId, rawIdeas);
      setOrganized(result.organized);
      setQuestions(result.suggested_questions);
      setCoverageScore(0.3); // Initial score before answering questions
      setStage('organized');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to organize ideas');
      setStage('braindump');
    }
  }, [planId, rawIdeas]);

  // Handle proceeding to questions
  const handleProceedToQuestions = useCallback(() => {
    if (organized && organized.gaps.length === 0) {
      // No gaps - go straight to generating canvas
      handleGenerateCanvas();
    } else {
      setStage('questions');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organized]);

  // Handle answer changes
  const handleAnswerChange = useCallback((questionId: string, answer: string) => {
    setAnswers((prev) => {
      const updated = { ...prev, [questionId]: answer };
      // Update coverage score based on answered questions
      const answeredCount = Object.values(updated).filter((a) => a.trim()).length;
      const totalQuestions = questions.length;
      setCoverageScore(0.3 + (answeredCount / totalQuestions) * 0.7);
      return updated;
    });
  }, [questions.length]);

  // Handle generating canvas
  const handleGenerateCanvas = useCallback(async () => {
    setStage('generating');
    setError(null);

    try {
      await api.generateCanvasFromIdeas(planId, answers);
      onCanvasGenerated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate canvas');
      setStage('questions');
    }
  }, [planId, answers, onCanvasGenerated]);

  // Handle going back
  const handleBackToBrainDump = useCallback(() => {
    setStage('braindump');
  }, []);

  const handleBackToOrganized = useCallback(() => {
    setStage('organized');
  }, []);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-center gap-2 mb-2">
          {['braindump', 'organized', 'questions', 'canvas'].map((step, i) => {
            const stepIndex = ['braindump', 'organized', 'questions', 'generating'].indexOf(stage);
            const isActive = i === stepIndex || (i === 3 && stage === 'generating');
            const isComplete = i < stepIndex || stage === 'generating';
            const isOrganizing = stage === 'organizing' && i === 0;

            return (
              <div key={step} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                    isComplete
                      ? 'bg-green-500 text-white'
                      : isActive
                        ? 'bg-accent text-accent-foreground'
                        : 'bg-cream/20 text-foreground-muted'
                  } ${isOrganizing ? 'animate-pulse' : ''}`}
                >
                  {isComplete ? '✓' : i + 1}
                </div>
                {i < 3 && (
                  <div
                    className={`w-12 h-0.5 ${isComplete ? 'bg-green-500' : 'bg-cream/20'}`}
                  />
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-center gap-8 text-xs text-foreground-tertiary">
          <span>Brain Dump</span>
          <span>Organize</span>
          <span>Questions</span>
          <span>Canvas</span>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/20 border border-red-500/40 rounded-lg text-red-300">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-4 text-sm underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Skip Option */}
      {stage === 'braindump' && onSkip && (
        <div className="mb-6 text-center">
          <button
            onClick={onSkip}
            className="text-sm text-foreground-tertiary hover:text-foreground transition-colors"
          >
            Already have a formed idea? Skip to traditional canvas →
          </button>
        </div>
      )}

      {/* Stage Content */}
      {stage === 'braindump' && (
        <BrainDump
          ideas={rawIdeas}
          onIdeasChange={setRawIdeas}
          onOrganize={handleOrganize}
        />
      )}

      {stage === 'organizing' && (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-accent border-t-transparent mb-4"></div>
          <p className="text-foreground text-lg">Analyzing your ideas...</p>
          <p className="text-foreground-tertiary text-sm mt-2">
            Finding themes, patterns, and gaps
          </p>
        </div>
      )}

      {stage === 'organized' && organized && (
        <IdeaOrganizer
          organized={organized}
          onProceedToQuestions={handleProceedToQuestions}
          onBackToBrainDump={handleBackToBrainDump}
        />
      )}

      {stage === 'questions' && (
        <GuidedQuestions
          questions={questions}
          answers={answers}
          onAnswerChange={handleAnswerChange}
          onGenerateCanvas={handleGenerateCanvas}
          onBackToOrganized={handleBackToOrganized}
          coverageScore={coverageScore}
        />
      )}

      {stage === 'generating' && (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-accent border-t-transparent mb-4"></div>
          <p className="text-foreground text-lg">Generating your Lean Canvas...</p>
          <p className="text-foreground-tertiary text-sm mt-2">
            Synthesizing ideas and answers into a cohesive business model
          </p>
        </div>
      )}
    </div>
  );
}
